import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import {
  ConfirmationModal,
  Page,
  useModals,
  Avatar,
  Button,
  Card,
  DotSeparator,
  DropdownMenu,
  FontAwesomeIcon,
  Skeleton,
  Stack,
  Tag,
  Text,
  Title,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { PipelineEditModal } from "@/pages/settings/recruit/pipelines/widgets/PipelineEditModal.tsx";
import { StageEditModal } from "@/pages/settings/recruit/pipelines/widgets/StageEditModal.tsx";
import { usePipelines } from "@/queries/recruit/use-pipelines.query.ts";
import { useStageDelete, useStages, useStageSort } from "@/queries/recruit/use-stages.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiPipeline } from "@/types/recruit/pipeline.types.ts";
import {
  ApiPipelineStage,
  getStageEmoji,
  PipelineStageTypes,
} from "@/types/recruit/stages.types.ts";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export default function Pipelines() {
  const { t } = useTranslation();
  useTitle(t("ats:pipelines"), t("common:settings"));

  const modalsService = useModals();
  const { data: pipelines, isLoading } = usePipelines();
  const { data: stages, isLoading: isLoadingStages } = useStages();
  const { mutateAsync: stageDelete } = useStageDelete();
  const stagesSort = useStageSort();

  const pipelinesByStageId = useMemo(() => {
    const map = new Map<number, string[]>();
    if (!pipelines) return map;
    for (const pipeline of pipelines) {
      for (const stage of pipeline.stages) {
        const names = map.get(stage.id);
        if (names) {
          names.push(pipeline.title);
        } else {
          map.set(stage.id, [pipeline.title]);
        }
      }
    }
    return map;
  }, [pipelines]);

  const handleEditOrCreatePipeline = useCallback(
    (pipeline: ApiPipeline | undefined = undefined) => {
      const pipelineToEdit = pipeline || {
        id: 0,
        title: "",
        is_default: false,
        stages: [],
        disqualified_candidates_count: 0,
        reserved_candidates_count: 0,
      };

      modalsService.openModal(PipelineEditModal, {
        pipeline: pipelineToEdit,
      });
    },
    []
  );

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.pipelines.viewed);
  });

  const handleEditOrCreateStage = useCallback((stage: ApiPipelineStage | undefined = undefined) => {
    const stageToEdit = stage || {
      id: 0,
      title: "",
      sort: 0,
      applicants_count: 0,
      type: PipelineStageTypes.custom,
      emoji: emojiDefaults.stages.default,
    };
    modalsService.openModal(StageEditModal, {
      stage: stageToEdit,
    });
  }, []);

  const handleDeleteStage = useCallback((stage: ApiPipelineStage) => {
    modalsService.openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          const result = await stageDelete(stage.id);
          if (result.success) {
            analyticsService.trackEvent(analyticEvents.pipelines.stageDeleted, {
              [analyticProperties.id]: stage.id,
            });
          }
        } catch {
          return;
        }
      },
    });
  }, []);

  const onDragEnd = (result: DropResult) => {
    if (!stages) return;

    const { destination, source } = result;

    if (!destination) return;

    if (destination.droppableId === source.droppableId && destination.index === source.index)
      return;

    const array = [...stages];
    const [removed] = array.splice(source.index, 1);
    array.splice(destination.index, 0, removed);

    stagesSort.mutate(
      array.map((stage) => stage.id),
      {
        onSuccess: () => {
          analyticsService.trackEvent(analyticEvents.pipelines.stagesSorted);
        },
      }
    );
  };

  return (
    <>
      <Page>
        <Page.Header showBack={true} title={t("ats:pipelines")} navigateTo={"/settings"}>
          <Stack>
            <Button
              onClick={() => {
                handleEditOrCreatePipeline();
              }}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
            >
              {t("common:create_new")}
            </Button>
          </Stack>
        </Page.Header>
        <Page.Content>
          <Stack direction={"vertical"} className={"py-6 gap-10"}>
            <Stack direction={"vertical"} gap={"md"}>
              <Title
                header={t("ats:pipelines")}
                size={"lg"}
                caption={t("ats:pipelines_description")}
              />

              <Stack
                direction={"horizontal"}
                gap={"md"}
                className={"grid md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6"}
              >
                {isLoading && <Card isLoading={true} />}

                {pipelines?.map((pipeline) => (
                  <Card
                    header={pipeline.title}
                    caption={pipeline.is_default ? t("ats:pipeline_default") : ""}
                    buttonsSlot={
                      <Tag>
                        {t("plurals:plural_candidates", {
                          count:
                            pipeline.stages.length > 0
                              ? pipeline.stages
                                  .map((stage) => stage.applicants_count)
                                  .reduce((sum, current) => sum + current)
                              : 0,
                        })}
                      </Tag>
                    }
                    onClick={() => {
                      handleEditOrCreatePipeline(pipeline);
                    }}
                    key={`pipeline-${pipeline.id}`}
                  />
                ))}

                <Card
                  icon={"fa-plus"}
                  header={t("common:create_new")}
                  variant={"action"}
                  onClick={() => {
                    handleEditOrCreatePipeline();
                  }}
                />
              </Stack>
            </Stack>
            <Stack direction={"vertical"} gap={"md"}>
              <Title header={t("ats:stages")} size={"lg"} caption={t("ats:stages_description")} />
              <Stack gap={"md"} className={stagesSort.isPending ? "opacity-50" : undefined}>
                {isLoadingStages && (
                  <Stack gap="sm" className={"w-full"}>
                    <Skeleton className={"w-1/2 h-6"}></Skeleton>
                    <Skeleton className={"w-full h-5"}></Skeleton>
                  </Stack>
                )}

                {stages && stages.length > 0 && (
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId={"droppable"}>
                      {(provided) => (
                        <div
                          className={"flex flex-col divide-y divide-inherit border-dark/5"}
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {stages?.map((stage, index) => (
                            <Draggable
                              key={stage.id}
                              draggableId={`item-${stage.id}`}
                              index={index}
                            >
                              {(provided) => (
                                <div
                                  className={"flex justify-between min-h-14 items-center py-2"}
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  style={{
                                    ...provided.draggableProps.style,
                                  }}
                                >
                                  <Stack direction={"horizontal"} gap={"sm"}>
                                    <div
                                      className={"w-10 flex items-center justify-center shrink-0"}
                                      {...provided.dragHandleProps}
                                    >
                                      <FontAwesomeIcon icon="fa-light fa-grip-dots-vertical" />
                                    </div>
                                    <Stack className={"w-10 shrink-0"} items={"center"}>
                                      <Avatar
                                        url={undefined}
                                        emoji={getStageEmoji(stage)}
                                        size={"md"}
                                      />
                                    </Stack>
                                    <Stack className={"w-[450px]"}>
                                      <Text className={"text-title"}>{stage.title}</Text>
                                      {stage.type === PipelineStageTypes.applied && (
                                        <Text className={"text-secondary"}>
                                          {t("ats:pipeline_stage_applied")}
                                        </Text>
                                      )}
                                      {stage.type === PipelineStageTypes.offer && (
                                        <Text className={"text-secondary"}>
                                          {t("ats:pipeline_stage_offer")}
                                        </Text>
                                      )}
                                      {stage.type === PipelineStageTypes.hired && (
                                        <Text className={"text-secondary"}>
                                          {t("ats:pipeline_stage_hired")}
                                        </Text>
                                      )}
                                    </Stack>
                                    <Stack className={"px-2"}>
                                      <DotSeparator className={"text-secondary"}>
                                        <Text>
                                          {t("plurals:plural_candidates", {
                                            count: stage.applicants_count,
                                          })}
                                        </Text>
                                        {(pipelinesByStageId.get(stage.id)?.length ?? 0) > 0 && (
                                          <Tooltip>
                                            <TooltipTrigger>
                                              <Text>
                                                {t("plurals:plural_pipelines", {
                                                  count:
                                                    pipelinesByStageId.get(stage.id)?.length ?? 0,
                                                })}
                                              </Text>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <Stack>
                                                {pipelinesByStageId.get(stage.id)?.map((name) => (
                                                  <Text key={name}>{name}</Text>
                                                ))}
                                              </Stack>
                                            </TooltipContent>
                                          </Tooltip>
                                        )}
                                      </DotSeparator>
                                    </Stack>
                                  </Stack>
                                  <Stack
                                    className={"justify-end"}
                                    gap={"sm"}
                                    direction={"horizontal"}
                                  >
                                    <Button
                                      onClick={() => {
                                        handleEditOrCreateStage(stage);
                                      }}
                                      variant={"secondary"}
                                    >
                                      {t("common:edit")}
                                    </Button>

                                    <DropdownMenu>
                                      <Button
                                        onClick={() => {
                                          handleDeleteStage(stage);
                                        }}
                                        leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                                        variant={"menuDanger"}
                                      >
                                        {t("common:delete")}...
                                      </Button>
                                    </DropdownMenu>
                                  </Stack>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
                <Button
                  variant={"secondary"}
                  size={"lg"}
                  onClick={() => {
                    handleEditOrCreateStage();
                  }}
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-plus"} />}
                >
                  {t("ats:stage_add")}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Page.Content>
      </Page>
    </>
  );
}
